import React, { useState } from 'react';
import { Container, Row, Col, Button, Form, Card, Modal, Alert } from 'react-bootstrap';
import { Select } from 'antd';
import { useNavigate } from 'react-router-dom';
import FormItem from '@/common/FormItem/FormItem';
import PageTitle from '@/common/PageTitle/PageTitle';
import styles from '@/styles/inventory-categories.module.scss';
import pageStyles from './categories.module.scss';

const initialCategories = [
  { name: 'KK Players', icon: '📱', items: 2 },
  { name: 'T-Shirts', icon: '👕', items: 0 },
  { name: 'Sweaters', icon: '🧥', items: 0 },
  { name: 'Promotional', icon: '🎁', items: 0 },
  { name: 'Hats', icon: '🧢', items: 0 },
  { name: 'Accessories', icon: '👓', items: 0 },
];

const Categories: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState(initialCategories);
  const [searchFilter, setSearchFilter] = useState<string | undefined>(undefined);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    icon: '📦',
    description: ''
  });

  // Filter categories based on search
  const filteredCategories = categories.filter(category => {
    if (!searchFilter) return true;
    return category.name.toLowerCase().includes(searchFilter.toLowerCase());
  });

  // Create search options from categories
  const searchOptions = categories.map(category => ({
    value: category.name,
    label: category.name
  }));

  const handleAddCategory = () => {
    if (!categoryForm.name.trim()) {
      setAlertMessage('Category name is required');
      setShowSuccessAlert(true);
      return;
    }

    // Check if category already exists
    const categoryExists = categories.some(cat => 
      cat.name.toLowerCase() === categoryForm.name.toLowerCase()
    );
    
    if (categoryExists) {
      setAlertMessage('A category with this name already exists');
      setShowSuccessAlert(true);
      return;
    }

    // Add new category to the list
    const newCategory = {
      name: categoryForm.name,
      icon: categoryForm.icon,
      items: 0
    };
    
    setCategories(prev => [...prev, newCategory]);
    setShowAddModal(false);
    setCategoryForm({ name: '', icon: '📦', description: '' });
    setAlertMessage('Category added successfully!');
    setShowSuccessAlert(true);
  };

  const handleFormChange = (field: string, value: string) => {
    setCategoryForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Container fluid>
      <Card className={pageStyles.card}>
        <Card.Body>
          <Card.Title className={pageStyles.cardTitle}>
            <PageTitle title="Categories" />
            <Button 
              className={styles.addButton}
              onClick={() => setShowAddModal(true)}
            >
              + Add Category
            </Button>
          </Card.Title>
          <div className={styles.lastUpdate}>
            Last update January 29, 2023 at 2:39PM
          </div>
      <Row className="mb-3">
        <Col md={4}>
          <FormItem
            label="Search Categories"
            className="customSelectWrapper"
            layout="vertical"
          >
            <Select
              showSearch
              allowClear
              className="customSelect"
              placeholder="Search categories"
              value={searchFilter}
              onChange={setSearchFilter}
              options={searchOptions}
              optionFilterProp="label"
              style={{ width: "100%" }}
            />
          </FormItem>
        </Col>
      </Row>
      <Row className={styles.categoriesRow}>
        {filteredCategories.map((cat) => (
          <Col key={cat.name} md={4} sm={6} xs={12} className={styles.categoryCol}>
            <div
              className={styles.categoryCard}
              onClick={() => navigate(`/inventory-categories/${encodeURIComponent(cat.name)}`)}
            >
              <div className={styles.categoryIcon}>
                {cat.icon}
              </div>
              <div className={styles.categoryName}>{cat.name}</div>
              <div className={styles.categoryItems}>{cat.items} items</div>
            </div>
          </Col>
        ))}
      </Row>
        </Card.Body>
      </Card>

      {/* Success Alert */}
      {showSuccessAlert && (
        <Alert 
          variant="success" 
          onClose={() => setShowSuccessAlert(false)} 
          dismissible
          className="mt-3"
        >
          {alertMessage}
        </Alert>
      )}

      {/* Add Category Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Category Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter category name"
                value={categoryForm.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Icon</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter emoji or icon"
                value={categoryForm.icon}
                onChange={(e) => handleFormChange('icon', e.target.value)}
              />
              <Form.Text className="text-muted">
                You can use emojis like 📱, 👕, 🧥, etc.
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter category description (optional)"
                value={categoryForm.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddCategory}>
            Add Category
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Categories; 